/**
 * Generic entry-source tracking carried on a Seaters destination URL.
 *
 * A tagged link says two things: which **channel** brought the visitor (`utm_source`) and which
 * individual **placement** inside that channel (`utm_content` — a KPN app tile id, a newsletter
 * block, an ad creative). The pair travels as ordinary query parameters on the Seaters destination,
 * which is what lets one mechanism serve every wrapper: the KPN OAuth deep link encodes the whole
 * destination into its `state`, the KPN email wrapper puts the same destination in `appUrl`/`webUrl`,
 * and a future advertisement wrapper only has to point at a destination too. Nothing here knows
 * about any wrapper.
 *
 * This module is the single home for parsing, validation, query-parameter merging and
 * serialization. It is imported by the SPA *and*, via Node's native TypeScript type stripping, by
 * `scripts/generate-kpn-*-deeplink.js`. That second consumer is why the file must stay
 * **dependency-free and erasable-syntax-only**: no imports (not even from elsewhere in
 * `@seaters/constants`), no `enum`, no `namespace`. Adding an import here breaks both generators at
 * runtime, not at type-check time.
 *
 * `overview` is deliberately not a source. A visitor who lands on the KPN catalogue untagged and
 * browses from there is direct/catalogue traffic, and inventing a bucket for them would turn "we
 * don't know" into a claim.
 */

/** The query parameter names, so no caller spells them out. */
export const ENTRY_TRACKING_SOURCE_PARAM = 'utm_source';
export const ENTRY_TRACKING_CONTENT_PARAM = 'utm_content';

/**
 * The closed set of channels. Closed on purpose: an open string is how analytics ends up with
 * `app`, `App`, `kpn-app` and `kpn_app` as four different channels. Widening it is a code change
 * plus a reporting decision, which is the point.
 *
 * `newsletter` and `advertisement` are accepted from day one so the mechanism is ready for them —
 * only the outer wrapper for those is still open, not the tracking contract.
 */
export const ENTRY_TRACKING_SOURCES = ['app', 'newsletter', 'advertisement'] as const;

export type EntryTrackingSource = (typeof ENTRY_TRACKING_SOURCES)[number];

export type EntryTracking = {
  source: EntryTrackingSource;
  /** The individual placement, e.g. a KPN tile id. Absent when a channel is tagged as a whole. */
  content?: string;
};

/**
 * Placement ids are a slug, capped short.
 *
 * The cap and the character class exist to keep unbounded or hostile data out of the analytics
 * columns: the value reaches us from a URL anyone can edit, is stored on the join interaction, and
 * is later rendered in a report. A slug cannot carry markup, a separator that would break a CSV, or
 * a megabyte of padding.
 */
export const ENTRY_TRACKING_CONTENT_MAX_LENGTH = 64;
const CONTENT_PATTERN = /^[a-z0-9]+(?:[-_.][a-z0-9]+)*$/;

const isEntryTrackingSource = (value: string): value is EntryTrackingSource =>
  (ENTRY_TRACKING_SOURCES as readonly string[]).includes(value);

export type EntryTrackingValidation =
  | { ok: true; tracking: EntryTracking }
  | { ok: false; errors: string[] };

/**
 * Validate a raw `{ source, content }` pair.
 *
 * Strict, and returns every problem at once rather than the first: the generators surface these to
 * whoever is minting a link, and telling them about one typo at a time is a poor trade for three
 * extra lines here.
 */
export const validateEntryTracking = (input: {
  source?: string | null;
  content?: string | null;
}): EntryTrackingValidation => {
  const errors: string[] = [];
  const source = input.source?.trim() ?? '';
  const content = input.content?.trim() ?? '';

  if (!source) {
    // Content alone cannot be reported on — every breakdown starts from the channel — so it is an
    // error rather than a silently dropped half-tag.
    errors.push(
      content
        ? `--content requires --source. Expected one of: ${ENTRY_TRACKING_SOURCES.join(', ')}.`
        : `Missing tracking source. Expected one of: ${ENTRY_TRACKING_SOURCES.join(', ')}.`
    );
  } else if (!isEntryTrackingSource(source)) {
    errors.push(
      `Unsupported tracking source "${source}". Expected one of: ${ENTRY_TRACKING_SOURCES.join(', ')}.`
    );
  }

  if (content) {
    if (content.length > ENTRY_TRACKING_CONTENT_MAX_LENGTH) {
      errors.push(
        `Tracking content is ${content.length} characters; the maximum is ${ENTRY_TRACKING_CONTENT_MAX_LENGTH}.`
      );
    } else if (!CONTENT_PATTERN.test(content)) {
      errors.push(
        `Invalid tracking content "${content}". Use lowercase letters, digits and - _ . separators, e.g. "voor-jou-tile-1".`
      );
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    tracking: content
      ? { source: source as EntryTrackingSource, content }
      : { source: source as EntryTrackingSource },
  };
};

/**
 * Read tracking off an untrusted query string — a real browser URL, so anything at all can be in
 * it.
 *
 * Unlike {@link validateEntryTracking} this never throws and never reports: a malformed tag is
 * dropped and the visit is treated as untagged. That asymmetry is deliberate. On the minting side a
 * bad value is an author's mistake worth failing on; on the reading side it is noise, and the only
 * safe thing to do with noise is to keep it out of the analytics store.
 *
 * A valid source with an invalid content is kept as a source-only tag rather than discarded whole —
 * the channel is still trustworthy, and losing it too would understate the channel.
 */
export const readEntryTracking = (
  search: string | URLSearchParams | null | undefined
): EntryTracking | null => {
  if (!search) {
    return null;
  }

  const params = typeof search === 'string' ? new URLSearchParams(search) : search;
  const rawSource = params.get(ENTRY_TRACKING_SOURCE_PARAM);

  if (!rawSource) {
    return null;
  }

  const full = validateEntryTracking({
    source: rawSource,
    content: params.get(ENTRY_TRACKING_CONTENT_PARAM),
  });

  if (full.ok) {
    return full.tracking;
  }

  const sourceOnly = validateEntryTracking({ source: rawSource });
  return sourceOnly.ok ? sourceOnly.tracking : null;
};

const splitDestination = (destination: string) => {
  const hashIndex = destination.indexOf('#');
  const withoutHash = hashIndex === -1 ? destination : destination.slice(0, hashIndex);
  const hash = hashIndex === -1 ? '' : destination.slice(hashIndex);

  const queryIndex = withoutHash.indexOf('?');
  const base = queryIndex === -1 ? withoutHash : withoutHash.slice(0, queryIndex);
  const query = queryIndex === -1 ? '' : withoutHash.slice(queryIndex + 1);

  return { base, query, hash };
};

const encode = (value: string) => encodeURIComponent(value);

/**
 * Add (or replace) the tracking parameters on a destination, preserving everything else.
 *
 * The existing query string is spliced textually rather than round-tripped through
 * `URLSearchParams`, which would re-encode every parameter it did not touch: a filter written as
 * `filter_ticketCategory=Voor Jou Vrijdag` would come back as `...=Voor+Jou+Vrijdag`. Both decode
 * identically, but the generated link is pasted into tickets and compared by eye, and a link that
 * changes shape for a parameter nobody edited invites someone to "fix" it back.
 *
 * Re-tagging is idempotent: any tracking parameters already present are dropped before the new pair
 * is appended, so a destination never carries two sources.
 */
export const withEntryTracking = (destination: string, tracking: EntryTracking): string => {
  const { base, query, hash } = splitDestination(destination);

  const preserved = query
    .split('&')
    .filter((pair) => pair.length > 0)
    .filter((pair) => {
      const key = pair.split('=')[0];
      return key !== ENTRY_TRACKING_SOURCE_PARAM && key !== ENTRY_TRACKING_CONTENT_PARAM;
    });

  const added = [`${ENTRY_TRACKING_SOURCE_PARAM}=${encode(tracking.source)}`];

  if (tracking.content) {
    added.push(`${ENTRY_TRACKING_CONTENT_PARAM}=${encode(tracking.content)}`);
  }

  return `${base}?${[...preserved, ...added].join('&')}${hash}`;
};

/**
 * The serialized form used everywhere a tag is stored or sent — the join request body, the
 * interaction blob, the reporting rows. Kept separate from {@link EntryTracking} so an absent
 * placement is an explicit `null` rather than a key that may or may not exist: `null` groups
 * predictably in SQL and survives a JSON round trip, `undefined` does neither.
 */
export type EntryTrackingPayload = {
  source: EntryTrackingSource;
  content: string | null;
};

export const toEntryTrackingPayload = (tracking: EntryTracking): EntryTrackingPayload => ({
  source: tracking.source,
  content: tracking.content ?? null,
});

export type TrackedDestination =
  | { ok: true; destination: string; tracking: EntryTracking | null }
  | { ok: false; errors: string[] };

/**
 * The whole minting step every link generator needs: validate the requested tag, then merge it into
 * the destination.
 *
 * Untagged in, untagged out — with neither flag supplied the destination is returned byte-identical,
 * which is what keeps every link generated before this existed reproducible.
 */
export const resolveTrackedDestination = (
  destination: string,
  input: { source?: string | null; content?: string | null }
): TrackedDestination => {
  if (!input.source?.trim() && !input.content?.trim()) {
    return { ok: true, destination, tracking: null };
  }

  const validation = validateEntryTracking(input);

  if (!validation.ok) {
    return validation;
  }

  return {
    ok: true,
    destination: withEntryTracking(destination, validation.tracking),
    tracking: validation.tracking,
  };
};
